import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Trash2, Calendar, Users, ListTodo, CheckCircle2, Circle, Loader2, MoreHorizontal
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  todo: 'bg-[hsl(var(--surface-3))] text-foreground',
  doing: 'bg-amber-100 text-amber-700 border-amber-200',
  done: 'bg-emerald-100 text-emerald-700 border-emerald-200'
};

const statusLabels = { todo: 'To Do', doing: 'In Progress', done: 'Done' };

const TodoDialog = ({ open, onOpenChange, channelId, dmId }) => {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [newItemText, setNewItemText] = useState('');
  const [newItemDue, setNewItemDue] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [view, setView] = useState('list');

  const loadTodos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (channelId) params.set('channel_id', channelId);
      if (dmId) params.set('dm_id', dmId);
      const res = await axios.get(`${API}/todos?${params}`);
      setTodos(res.data.todos || []);
    } catch (e) {
      toast.error('Failed to load todos');
    } finally {
      setLoading(false);
    }
  }, [channelId, dmId]);

  useEffect(() => {
    if (open) loadTodos();
  }, [open, loadTodos]);

  const handleCreateTodo = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/todos`, {
        title: newTitle.trim(),
        channel_id: channelId || null,
        dm_id: dmId || null
      });
      setTodos(prev => [res.data.todo, ...prev]);
      setNewTitle('');
      setSelectedTodo(res.data.todo);
      setView('detail');
      toast.success('Todo list created');
    } catch (e) {
      toast.error('Failed to create todo');
    } finally {
      setCreating(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItemText.trim() || !selectedTodo) return;
    setAddingItem(true);
    try {
      const res = await axios.post(`${API}/todos/${selectedTodo.todo_id}/items`, {
        text: newItemText.trim(),
        due_at: newItemDue || null
      });
      setSelectedTodo(prev => ({
        ...prev,
        items: [...(prev.items || []), res.data.item]
      }));
      setTodos(prev => prev.map(t =>
        t.todo_id === selectedTodo.todo_id
          ? { ...t, items: [...(t.items || []), res.data.item] }
          : t
      ));
      setNewItemText('');
      setNewItemDue('');
    } catch (e) {
      toast.error('Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleUpdateItem = async (todoId, itemId, updates) => {
    try {
      await axios.put(`${API}/todos/${todoId}/items/${itemId}`, updates);
      const updateItems = (items) =>
        items.map(item => item.item_id === itemId ? { ...item, ...updates } : item);

      setSelectedTodo(prev => prev ? { ...prev, items: updateItems(prev.items || []) } : prev);
      setTodos(prev => prev.map(t =>
        t.todo_id === todoId ? { ...t, items: updateItems(t.items || []) } : t
      ));
    } catch (e) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      await axios.delete(`${API}/todos/${todoId}`);
      setTodos(prev => prev.filter(t => t.todo_id !== todoId));
      if (selectedTodo?.todo_id === todoId) {
        setSelectedTodo(null);
        setView('list');
      }
      toast.success('Todo list deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const cycleStatus = (current) => {
    const order = ['todo', 'doing', 'done'];
    const idx = order.indexOf(current || 'todo');
    return order[(idx + 1) % order.length];
  };

  const getProgress = (items) => {
    if (!items?.length) return 0;
    const done = items.filter(i => i.status === 'done').length;
    return Math.round((done / items.length) * 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[80vh] flex flex-col" data-testid="todo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <ListTodo className="w-5 h-5 text-[hsl(var(--primary))]" />
            To-Do Lists
          </DialogTitle>
        </DialogHeader>

        {view === 'list' ? (
          <>
            {/* Create new */}
            <div className="flex gap-2">
              <Input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="New to-do list title..."
                className="h-9 text-sm flex-1"
                onKeyDown={e => e.key === 'Enter' && handleCreateTodo()}
                data-testid="new-todo-title-input"
              />
              <Button size="sm" onClick={handleCreateTodo} disabled={!newTitle.trim() || creating} className="h-9 gap-1" data-testid="create-todo-btn">
                <Plus className="w-3.5 h-3.5" /> Create
              </Button>
            </div>

            <ScrollArea className="flex-1 max-h-[400px]">
              {loading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : todos.length > 0 ? (
                <div className="space-y-1.5">
                  {todos.map(todo => {
                    const progress = getProgress(todo.items);
                    return (
                      <div
                        key={todo.todo_id}
                        className="w-full text-left p-3 rounded-lg border border-border hover:bg-[hsl(var(--message-hover))] transition-colors cursor-pointer"
                        onClick={() => { setSelectedTodo(todo); setView('detail'); }}
                        role="button"
                        tabIndex={0}
                        data-testid={`todo-list-item-${todo.todo_id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium truncate">{todo.title}</p>
                          {todo.created_by === user?.user_id && (
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6 -mr-1"
                              onClick={(e) => { e.stopPropagation(); handleDeleteTodo(todo.todo_id); }}
                              data-testid={`delete-todo-${todo.todo_id}`}
                            >
                              <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{todo.items?.length || 0} items</span>
                          <div className="flex-1 h-1.5 bg-[hsl(var(--surface-2))] rounded-full overflow-hidden">
                            <div className="h-full bg-[hsl(var(--primary))] rounded-full transition-all" style={{ width: `${progress}%` }} />
                          </div>
                          <span>{progress}%</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">by {todo.creator_name}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ListTodo className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                  <p className="text-sm text-muted-foreground">No to-do lists yet</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Create one to start tracking tasks</p>
                </div>
              )}
            </ScrollArea>
          </>
        ) : selectedTodo ? (
          <>
            {/* Detail view */}
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setSelectedTodo(null); setView('list'); }} data-testid="back-to-list-btn">
                Back
              </Button>
              <Separator orientation="vertical" className="h-4" />
              <h4 className="text-sm font-semibold flex-1 truncate">{selectedTodo.title}</h4>
            </div>

            {/* Add item */}
            <div className="flex gap-2">
              <Input
                value={newItemText}
                onChange={e => setNewItemText(e.target.value)}
                placeholder="Add a task..."
                className="h-8 text-sm flex-1"
                onKeyDown={e => e.key === 'Enter' && handleAddItem()}
                data-testid="new-item-text-input"
              />
              <Input
                type="date"
                value={newItemDue}
                onChange={e => setNewItemDue(e.target.value)}
                className="h-8 text-sm w-32"
                data-testid="new-item-due-input"
              />
              <Button size="sm" className="h-8" onClick={handleAddItem} disabled={!newItemText.trim() || addingItem} data-testid="add-item-btn">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>

            <ScrollArea className="flex-1 max-h-[350px]">
              <div className="space-y-1">
                {(selectedTodo.items || []).map(item => (
                  <div
                    key={item.item_id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border transition-colors ${item.status === 'done' ? 'opacity-60' : ''}`}
                    data-testid={`todo-item-${item.item_id}`}
                  >
                    <button
                      className="flex-shrink-0"
                      onClick={() => handleUpdateItem(selectedTodo.todo_id, item.item_id, { status: cycleStatus(item.status) })}
                      data-testid={`cycle-status-${item.item_id}`}
                    >
                      {item.status === 'done' ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <Circle className={`w-5 h-5 ${item.status === 'doing' ? 'text-amber-500' : 'text-muted-foreground'}`} />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${item.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>
                        {item.text}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`${statusColors[item.status || 'todo']} text-[10px] h-4 border`}>
                          {statusLabels[item.status || 'todo']}
                        </Badge>
                        {item.due_at && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <Calendar className="w-2.5 h-2.5" />
                            {new Date(item.due_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(!selectedTodo.items || selectedTodo.items.length === 0) && (
                  <div className="text-center py-6">
                    <p className="text-sm text-muted-foreground">No tasks yet. Add one above.</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default TodoDialog;
